import { useState, type FormEvent } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { apiClient, normalizeApiError } from '../../lib/apiClient'
import type { ApiResponse } from '../../types'
import type {
  Appointment,
  PatientFile,
} from '../appointments/appointment.types'
import Panel from '../../components/cards/Panel'
import ProfilePhoto from './ProfilePhoto'
import PrivateFilePreview from './PrivateFilePreview'
import PageHeader from '../../components/common/PageHeader'
import ErrorState from '../../components/common/ErrorState'
import EmptyState from '../../components/common/EmptyState'
import { Skeleton } from '../../components/common/Loading'
type Role = 'admin' | 'doctor' | 'patient'
type Notice = {
  id: number
  title: string
  message: string
  read_at: string | null
}
type Profile = {
  name: string
  email: string | null
  mobile: string
  patient?: Record<string, string | null>
}
export function DashboardPage({ role }: { role: Role }) {
  const q = useQuery({
    queryKey: [role, 'dashboard'],
    queryFn: async () =>
      (
        await apiClient.get<ApiResponse<Record<string, unknown>>>(
          '/' + role + '/dashboard',
        )
      ).data.data,
  })
  return (
    <div className="page-container">
      <PageHeader
        title={role[0].toUpperCase() + role.slice(1) + ' dashboard'}
        description="Your practice and care at a glance"
      />
      {q.isLoading ? (
        <Skeleton lines={8} />
      ) : q.isError ? (
        <ErrorState onRetry={() => q.refetch()} />
      ) : (
        <>
          <div className="stats-grid">
            {Object.entries(q.data ?? {})
              .filter(([, v]) => typeof v === 'number' || typeof v === 'string')
              .map(([k, v]) => (
                <article className="stat-card" key={k}>
                  <div>
                    <strong>{String(v)}</strong>
                    <span>{k.replaceAll('_', ' ')}</span>
                  </div>
                </article>
              ))}
          </div>
          <Panel
            className="mt-3"
            title="Appointments"
            action={<Link to={'/' + role + '/appointments'}>View all</Link>}
          >
            {((q.data?.appointments ?? []) as Appointment[]).map((a) => (
              <p key={a.id}>
                <Link to={'/' + role + '/appointments/' + a.id}>
                  {a.appointment_no}
                </Link>{' '}
                · {a.appointment_date} {a.start_time} · {a.status}
              </p>
            ))}
            {role === 'patient' && (
              <Link className="btn btn-primary" to="/patient/appointments/new">
                Book appointment
              </Link>
            )}
          </Panel>
          <Panel className="mt-3" title="Appointment status">
            {(
              (q.data?.status_summary ?? []) as {
                status: string
                total: number
              }[]
            ).map((s) => (
              <div key={s.status} className="mb-2">
                <span>
                  {s.status}: {s.total}
                </span>
                <progress
                  className="d-block w-100"
                  value={s.total}
                  max={Number(q.data?.total_appointments) || 1}
                />
              </div>
            ))}
          </Panel>
          <Panel className="mt-3" title="Recent notifications">
            {((q.data?.recent_notifications ?? []) as Notice[]).map((n) => (
              <p key={n.id}>
                <strong>{n.title}</strong> {n.message}
              </p>
            ))}
            <Link to={'/' + role + '/notifications'}>All notifications</Link>
          </Panel>
        </>
      )}
    </div>
  )
}
export function ProfilePage({ role }: { role: Role }) {
  const q = useQuery({
    queryKey: [role, 'profile'],
    queryFn: async () =>
      (await apiClient.get<ApiResponse<Profile>>('/' + role + '/profile')).data
        .data,
  })
  const m = useMutation({
    mutationFn: (d: Record<string, FormDataEntryValue>) =>
      apiClient.put('/' + role + '/profile', d),
    onSuccess: () => q.refetch(),
  })
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    m.mutate(Object.fromEntries(new FormData(e.currentTarget)))
  }
  const accountFields = ['name', 'email', 'mobile']
  const patientFields = ['date_of_birth', 'gender', 'blood_group', 'address', 'emergency_contact_name', 'emergency_contact_mobile']
  const value = (key: string) => String((q.data as unknown as Record<string, unknown>)?.[key] ?? q.data?.patient?.[key] ?? '')
  const field = (key: string) => <label key={key}>{key.replaceAll('_', ' ')}{key === 'gender' ? <select className="form-select" name={key} defaultValue={value(key)}><option value="">Select gender</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option><option value="prefer_not_to_say">Prefer not to say</option></select> : key === 'address' ? <textarea className="form-control" name={key} rows={3} defaultValue={value(key)} /> : <input className="form-control" name={key} type={key === 'date_of_birth' ? 'date' : key === 'email' ? 'email' : 'text'} defaultValue={value(key)} />}</label>
  return (
    <div className="page-container">
      <PageHeader title={role === 'patient' ? 'Update patient profile' : 'My profile'} description="Keep your personal and contact information accurate." />
      <Panel title="Profile information" className="profile-editor-panel">
        {q.isLoading ? (
          <Skeleton />
        ) : q.isError ? (
          <ErrorState />
        ) : (
          <form onSubmit={submit} key={q.dataUpdatedAt} className="professional-profile-form">
            <div className="profile-photo-column">{role === 'patient' && q.data && <ProfilePhoto endpoint="/patient/profile/photo" photoUrl={q.data.patient?.photo_url} onSaved={() => q.refetch()} />}<Link className="btn btn-outline-secondary" to={'/' + role + '/change-password'}>Change password</Link></div>
            <div className="profile-fields"><h2>Account details</h2><div className="form-grid">{accountFields.map(field)}</div>{role === 'patient' && <><h2>Patient details</h2><div className="form-grid">{patientFields.map(field)}</div></>}
            {m.isError && (
              <ErrorState message={normalizeApiError(m.error).message} />
            )}{' '}
            {m.isSuccess && <p role="status">Profile saved.</p>}
            <button className="btn btn-primary mt-3 profile-save" disabled={m.isPending}>
              {m.isPending ? 'Updating…' : 'Update profile'}
            </button>
            </div>
          </form>
        )}
      </Panel>
    </div>
  )
}
export function PasswordPage({ role }: { role: Role }) {
  const m = useMutation({
    mutationFn: (d: Record<string, FormDataEntryValue>) =>
      apiClient.put('/' + role + '/change-password', d),
  })
  return (
    <div className="page-container">
      <PageHeader title="Change password" />
      <Panel title="Account security">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            m.mutate(Object.fromEntries(new FormData(e.currentTarget)))
          }}
        >
          {['current_password', 'password', 'password_confirmation'].map(
            (k) => (
              <label className="d-block mb-3" key={k}>
                {k.replaceAll('_', ' ')}
                <input
                  className="form-control"
                  type="password"
                  name={k}
                  required
                  minLength={8}
                />
              </label>
            ),
          )}
          {m.isError && (
            <ErrorState message={normalizeApiError(m.error).message} />
          )}{' '}
          {m.isSuccess && <p role="status">Password changed.</p>}
          <button className="btn btn-primary" disabled={m.isPending}>
            Save password
          </button>
        </form>
      </Panel>
    </div>
  )
}
export function NotificationsPage({ role }: { role: Role }) {
  const [page, setPage] = useState(1)
  const q = useQuery({
    queryKey: [role, 'notifications', page],
    queryFn: async () =>
      (
        await apiClient.get<ApiResponse<Notice[]>>(
          '/' + role + '/notifications',
          { params: { page } },
        )
      ).data,
  })
  const prefs = useQuery({
    queryKey: [role, 'prefs'],
    queryFn: async () =>
      (
        await apiClient.get<
          ApiResponse<{ email: boolean; sms: boolean; in_app: boolean }>
        >('/' + role + '/notification-preferences')
      ).data.data,
  })
  const read = useMutation({
    mutationFn: (id: number) =>
      apiClient.patch('/' + role + '/notifications/' + id + '/read'),
    onSuccess: () => q.refetch(),
  })
  const save = useMutation({
    mutationFn: (d: Record<string, boolean>) =>
      apiClient.put('/' + role + '/notification-preferences', d),
    onSuccess: () => prefs.refetch(),
  })
  return (
    <div className="page-container">
      <PageHeader title="Notifications" />
      <Panel title="Messages">
        {q.isLoading ? (
          <Skeleton />
        ) : q.isError ? (
          <ErrorState />
        ) : q.data?.data.length ? (
          q.data.data.map((n) => (
            <article className="appointment-card" key={n.id}>
              <div className="flex-grow-1">
                <strong>{n.title}</strong>
                <p>{n.message}</p>
              </div>
              <button
                className="btn btn-sm btn-outline-primary"
                disabled={!!n.read_at || read.isPending}
                onClick={() => read.mutate(n.id)}
              >
                {n.read_at ? 'Read' : 'Mark read'}
              </button>
            </article>
          ))
        ) : (
          <EmptyState title="No notifications" />
        )}
        {read.isError && <ErrorState />}
        <div className="form-actions">
          <button
            disabled={page === 1}
            className="btn btn-outline-secondary"
            onClick={() => setPage(page - 1)}
          >
            Previous
          </button>
          <button
            disabled={!q.data?.meta || page >= q.data.meta.last_page}
            className="btn btn-outline-secondary"
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      </Panel>
      <Panel className="mt-3" title="Notification preferences">
        {prefs.data && (
          <form
            key={prefs.dataUpdatedAt}
            onSubmit={(e) => {
              e.preventDefault()
              const d = new FormData(e.currentTarget)
              save.mutate(
                Object.fromEntries(
                  ['email', 'sms', 'in_app'].map((k) => [k, d.has(k)]),
                ),
              )
            }}
          >
            {(['in_app', 'email', 'sms'] as const).map((k) => (
              <label className="me-3" key={k}>
                <input
                  name={k}
                  type="checkbox"
                  defaultChecked={prefs.data[k]}
                />{' '}
                {k.replace('_', ' ')}
              </label>
            ))}
            <button className="btn btn-primary" disabled={save.isPending}>
              Save
            </button>
            {save.isSuccess && <p role="status">Preferences saved.</p>}
            {save.isError && <ErrorState />}
          </form>
        )}
      </Panel>
    </div>
  )
}
export function FilesPage() {
  const [preview, setPreview] = useState<PatientFile | null>(null)
  const [page, setPage] = useState(1)
  const q = useQuery({
    queryKey: ['patient-files', page],
    queryFn: async () =>
      (
        await apiClient.get<ApiResponse<PatientFile[]>>('/patient/files', {
          params: { page },
        })
      ).data,
  })
  const upload = useMutation({
    mutationFn: (d: FormData) => apiClient.post('/patient/files', d),
    onSuccess: () => q.refetch(),
  })
  const remove = useMutation({
    mutationFn: (id: number) => apiClient.delete('/patient/files/' + id),
    onSuccess: () => q.refetch(),
  })
  return (
    <div className="page-container">
      <PageHeader title="Private reports" />
      {preview && <PrivateFilePreview key={preview.id} file={preview} onClose={() => setPreview(null)} />}
      <Panel title="Upload report">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            upload.mutate(new FormData(e.currentTarget))
          }}
        >
          <label>
            PDF, JPG or PNG, maximum 10 MB
            <input
              className="form-control"
              type="file"
              name="file"
              required
              accept=".pdf,.jpg,.jpeg,.png"
            />
          </label>
          <label className="d-block mt-2">
            Note
            <input className="form-control" name="note" />
          </label>
          <button className="btn btn-primary mt-3" disabled={upload.isPending}>
            Upload privately
          </button>
        </form>
        {upload.isSuccess && <p role="status">Report uploaded.</p>}
        {(upload.isError || remove.isError) && (
          <ErrorState
            message={normalizeApiError(upload.error ?? remove.error).message}
          />
        )}
      </Panel>
      <Panel className="mt-3" title="My reports">
        {q.isLoading ? (
          <Skeleton />
        ) : q.isError ? (
          <ErrorState />
        ) : q.data?.data.length ? (
          q.data.data.map((f) => (
            <div className="appointment-card" key={f.id}>
              <span className="flex-grow-1">{f.original_name}</span>
              <button className="btn btn-sm btn-outline-primary" onClick={() => setPreview(f)}>
                Preview
              </button>
              <a href={f.download_url}>Download</a>
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => {
                  if (window.confirm('Delete this private report?')) {
                    if (preview?.id === f.id) setPreview(null)
                    remove.mutate(f.id)
                  }
                }}
              >
                Delete
              </button>
            </div>
          ))
        ) : (
          <EmptyState title="No reports" />
        )}
        <div className="form-actions">
          <button
            className="btn btn-outline-secondary"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </button>
          <span>Page {page}</span>
          <button
            className="btn btn-outline-secondary"
            disabled={!q.data?.meta || page >= q.data.meta.last_page}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      </Panel>
    </div>
  )
}
interface PatientRow {
  id: number
  patient_no: string
  user: { name: string; email: string; mobile: string }
}
export function PatientsPage({ role }: { role: 'doctor' | 'admin' }) {
  const { patientId } = useParams()
  const [search, setSearch] = useState('')
  const [page,setPage]=useState(1)
  const query = useQuery({
    queryKey: [role, 'patients', patientId, search,page],
    queryFn: async () =>
      (
        await apiClient.get<
          ApiResponse<
            PatientRow[] | { patient: PatientRow; appointments: Appointment[] }
          >
        >('/' + role + '/patients' + (patientId ? '/' + patientId : ''), {
          params: { search,page },
        })
      ).data,
  })
  const q={...query,data:query.data?.data}
  return (
    <div className="page-container">
      <PageHeader title={patientId ? 'Patient history' : 'Patients'} />
      <Panel title="Patient records">
        <input
          className="form-control mb-3"
          placeholder="Search patient"
          value={search}
          onChange={(e) => {setSearch(e.target.value);setPage(1)}}
        />
        {q.isLoading ? (
          <Skeleton />
        ) : q.isError ? (
          <ErrorState />
        ) : Array.isArray(q.data) ? (
          q.data.length?q.data.map((p) => (
            <p key={p.id}>
              <Link to={'/' + role + '/patients/' + p.id}>{p.user.name}</Link> ·{' '}
              {p.patient_no}
            </p>
          )):<EmptyState title="No matching patients"/>
        ) : (
          q.data && (
            <>
              <h3>{q.data.patient.user.name}</h3>
              <p>
                {q.data.patient.patient_no} · {q.data.patient.user.mobile}
              </p>
              {q.data.appointments.length?q.data.appointments.map((a) => (
                <p key={a.id}>
                  <Link to={'/' + role + '/appointments/' + a.id}>
                    {a.appointment_no}
                  </Link>{' '}
                  · {a.appointment_date} · {a.status}
                </p>
              )):<EmptyState title="No appointment history"/>}
            </>
          )
        )}
        {!patientId&&<div className="form-actions"><button className="btn btn-outline-secondary" disabled={page===1} onClick={()=>setPage(page-1)}>Previous</button><span>Page {page}</span><button className="btn btn-outline-secondary" disabled={!query.data?.meta||page>=query.data.meta.last_page} onClick={()=>setPage(page+1)}>Next</button></div>}
      </Panel>
    </div>
  )
}
